import { useEffect, useState } from "react";

interface DemoPlayerProps {
  title: string;
  image: string;
  subtitle?: string;
  poster: string;
}

const TOTAL_SECONDS = 92 * 60 + 45;

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function DemoPlayer({ title, image, subtitle, poster }: DemoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(6);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 0.6));
    }, 1000);
    return () => clearInterval(timer);
  }, [playing]);

  const current = Math.floor((progress / 100) * TOTAL_SECONDS);

  return (
    <div className="rounded-lg overflow-hidden bg-background-100 border border-background-300/50">
      {/* Screen */}
      <div className="relative w-full aspect-video bg-background-50 overflow-hidden">
        <img
          src={image}
          alt={`${title} 播放剧照`}
          title={`${title} 播放演示`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-50/70" />

        {/* Demo notice */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-950 text-[11px] font-heading font-bold">
            播放演示
          </span>
          <span className="px-2.5 py-1 rounded-md bg-background-50/80 backdrop-blur text-foreground-800 text-[11px]">
            暂无真实视频流
          </span>
        </div>

        {/* Center play */}
        <button
          type="button"
          onClick={() => setPlaying((v) => !v)}
          aria-label={playing ? "暂停" : "播放"}
          className="absolute inset-0 flex items-center justify-center cursor-pointer group"
        >
          <span
            className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
              playing
                ? "bg-background-50/30 backdrop-blur opacity-0 group-hover:opacity-100"
                : "bg-primary-500 text-background-50"
            }`}
          >
            <i className={playing ? "ri-pause-fill text-3xl" : "ri-play-fill text-4xl"} />
          </span>
        </button>

        {/* Poster thumb */}
        <div className="hidden md:block absolute bottom-16 left-4 w-20 aspect-[5/7] rounded-md overflow-hidden border border-background-300/60">
          <img
            src={poster}
            alt={`${title} 海报`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {playing && (
          <div className="absolute bottom-14 left-4 md:left-28 flex items-center gap-2 text-xs text-foreground-800">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
            正在播放演示画面
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="px-4 md:px-5 py-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] text-foreground-600 font-label tabular-nums whitespace-nowrap">
            {formatTime(current)}
          </span>
          <div
            className="relative flex-1 h-1.5 rounded-full bg-background-300 cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const ratio = (e.clientX - rect.left) / rect.width;
              setProgress(Math.min(100, Math.max(0, ratio * 100)));
            }}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-primary-500"
              style={{ width: `${progress}%` }}
            />
            <span
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary-500 -ml-1.5"
              style={{ left: `${progress}%` }}
            />
          </div>
          <span className="text-[11px] text-foreground-600 font-label tabular-nums whitespace-nowrap">
            {formatTime(TOTAL_SECONDS)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPlaying((v) => !v)}
              aria-label={playing ? "暂停" : "播放"}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:text-primary-500 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className={playing ? "ri-pause-line text-xl" : "ri-play-line text-xl"} />
            </button>
            <button
              type="button"
              aria-label="快退"
              onClick={() => setProgress((p) => Math.max(0, p - 5))}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:text-primary-500 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-replay-10-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="快进"
              onClick={() => setProgress((p) => Math.min(100, p + 5))}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:text-primary-500 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-forward-10-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => setMuted((v) => !v)}
              aria-label="静音"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:text-primary-500 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className={muted ? "ri-volume-mute-line text-xl" : "ri-volume-up-line text-xl"} />
            </button>
          </div>
          <div className="text-xs text-foreground-600 truncate hidden sm:block">
            {subtitle || title}
          </div>
          <button
            type="button"
            aria-label="全屏"
            className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:text-primary-500 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-fullscreen-line text-xl" />
          </button>
        </div>
      </div>
    </div>
  );
}